<template>
  <div
    class="patch-editor"
    :class="{differentEdit: $route.name === 'PatchEditor' || $route.name === 'InsertEditor'}"
    v-on:click="onClickOutsideCanvas"
  >
    <b-spinner
      label="Loading..."
      class="editor-loader"
      v-if="isPending || pendingApartSvgPatchText"
    />

    <div class="editor-colors">
      <div class="list-colors">
        <div class="list-colors__header">{{ colorsSidebarName }}</div>

        <ul class="colorpicker">
          <PatchColorItem
            v-for="(colorData, i) in colors"
            :id="`patch-${colorData.layerId}`"
            :key="`patch-${colorData.layerId}`"
            :color="colorData.color"
            :colorId="colorData.id"
            :material="colorData.material"
            :layer-id="colorData.layerId"
            :index="i"
            :selectIsActive="$route.name.includes('Editor')"
            :selectedTag="colorData[`${colorData.layerId}`]"
            :editableProperty="colorData.layerId.includes('STROKE') ? 'stroke' : 'fill'"
            @onChangeColor="onChangeColor"
            @onChangeTag="onChangeTag"
            class="color-item"
          />
        </ul>
      </div>
    </div>

    <drop
      @drop="handleInsertDrop"
      class="svg-dropzone"
      id="svg-dropzone"
      ref="svg-dropzone"
    >
      <div id="svg-container" ref="patchContainer">
      </div>
    </drop>

    <TextEditor
      v-if="!$route.name.includes('Editor')"
      :text="text"
      :text-color="textColor"
      :text-size="textSize"
      @onAddText="onAddText"
      @onChangeText="onChangeText"
      @onChangeTextColor="onChangeTextColor"
      @onChangeFontSize="onChangeFontSize"
    />

    <InsertsSidebar
      v-if="!$route.name.includes('Editor')"
      @onChangeInsertColor="onChangeInsertColor"
      @addInsertOnPatch="onAddInsert"
      :activeInsertElement="activeInsertElement"
    />

    <b-alert
      v-if="!!updatedPatchError"
      :show="!!updatedPatchError"
      variant="danger"
      dismissible
    >
      <div>{{ updatedPatchError.message }}</div>
      <div class="error-details">{{ updatedPatchError.details }}</div>
    </b-alert>

    <b-alert
      v-if="!!updatedInsertError"
      :show="!!updatedInsertError"
      variant="danger"
      dismissible
    >
      <div>{{ updatedInsertError.message }}</div>
      <div class="error-details">{{ updatedInsertError.details }}</div>
    </b-alert>

    <div v-if="!$route.name.includes('Editor')"
         class="modal-footer border-top-0 p-0 pt-3 mt-3 d-flex justify-content-between">
      <b-button variant="danger" @click="cancel">{{ cancelButtonName }}</b-button>

      <b-button :disabled="isSavingPatch" variant="success"
                @click="save">{{ saveButtonName }}</b-button>
    </div>

    <div v-else class="modal-footer border-top-0 p-0 pt-3 mt-3 d-flex justify-content-between">
      <b-button variant="danger" @click="apartCancel">

        {{ cancelButtonName }}

      </b-button>

      <b-button style="display: flex; align-items: center"
                :disabled="isSavingPatch || pendingApartPatchSaving" variant="success"
                @click="saveApartPatchOrInsert">

        {{ saveButtonName }}

        <b-spinner v-if="pendingApartPatchSaving"
                              small
                              style="margin-left: 10px">
        </b-spinner>
      </b-button>
    </div>
  </div>
</template>

<script>
import * as SVG from 'svg.js/dist/svg';
import { uniqueId } from '@/common/utils';
import { ONE_INCH_IN_PX } from '@/common/constants';
import PatchColorItem from '@/components/canvas/PatchColorItem.vue';
import TextEditor from '@/components/canvas/TextEditor.vue';
import InsertsSidebar from '@/components/canvas/InsertsSidebar.vue';
import SvgMixin from '@/components/canvas/SvgMixin';
import svgApi from '@/api/svgApi';
import * as htmlToImage from 'html-to-image';
import flattenDeep from 'lodash/flattenDeep';

export default {
  name: 'PatchEditor',
  components: {
    InsertsSidebar,
    TextEditor,
    PatchColorItem
  },

  mixins: [SvgMixin],

  props: {
    modalId: String,
    isPending: Boolean,
    patchSvgText: String,
    patchName: String,
    patchId: String,
    originalPatchId: String,
    patchCategoryId: String,
    size: Object,
    availableSizes: String,
  },

  data () {
    return {
      canvas: null,
      canvasId: null,
      colors: [],
      schoolColors: null,

      textElements: [],
      activeTextElement: null,
      text: '',
      textColor: {},
      textSize: 0.3,
      tagsMap: {},

      insertElements: [],
      insertColors: [],
      activeInsertElement: null,
      activeSide: null,
      isSavingPatch: false,
      savingInserts: [],
      apartSvgPatchText: null,
      pendingApartSvgPatchText: false,
      pendingApartPatchSaving: false,
    };
  },

  computed: {
    cancelButtonName () {
      if (this.$route.name === 'InsertEditor') {
        return 'CANCEL INSERT';
      }
      return 'CANCEL PATCH';
    },

    saveButtonName () {
      if (this.$route.name === 'InsertEditor') {
        return 'SAVE INSERT';
      }

      if (this.$route.name === 'PatchEditor') {
        return 'SAVE PATCH';
      }
      return 'ADD TO JACKET';
    },

    colorsSidebarName () {
      if (this.$route.name === 'InsertEditor') {
        return 'INSERT COLORS';
      }

      return 'PATCH COLORS';
    },

    updatedInsertError () {
      return this.$store.getters['design/getUpdatedInsertError'];
    },

    updatedInsertData () {
      return this.$store.getters['design/getUpdatedInsertData'];
    },

    insertForEditing () {
      return this.$store.getters['design/getInsertForEditing'];
    },

    updatedPatchError () {
      return this.$store.getters['design/getUpdatedPatchError'];
    },

    updatedPatchData () {
      return this.$store.getters['design/getUpdatedPatchData'];
    },

    patchForEditing () {
      return this.$store.getters['design/getPatchForEditing'];
    },

    jacketSide () {
      return this.$store.getters['ui/getJacketSide'];
    },

    totalPrice () {
      return this.$store.getters['finalizeOrder/getTotalPrice'];
    },
    patches () {
      return this.$store.getters['design/getProjectPatches'];
    },
    patchesBySide () {
      return (jacketSide) => this.$store.getters['design/getPatchesBySide'](jacketSide);
    },
    activePatch () {
      return this.$store.getters['elements/getActivePatch'];
    },

    inserts () {
      return this.$store.getters['elements/getInserts'];
    },

    insertCategories () {
      return this.$store.getters['elements/getInsertCategories'];
    },

    colorsCategories () {
      return this.$store.getters['elements/getColorsCategories'];
    }
  },

  watch: {
    jacketSide () {
      this.activeSide = this.jacketSide;
    },

    patchSvgText () {
      this.loadSvgText();
    }
  },

  async mounted () {
    if (this.patchSvgText || this.$route.name === 'PatchEditor' || this.$route.name === 'InsertEditor') {
      this.loadSvgText();
    }
  },

  methods: {
    async loadSvgText () {
      this.pendingApartSvgPatchText = true;
      this.schoolColors = this.$store.getters['elements/getSchoolColors'];
      const colorsCategories = this.$store.dispatch('elements/getColors');

      if (this.$route.name === 'PatchEditor' || this.$route.name === 'InsertEditor') {
        if (this.$route.name === 'PatchEditor') {
          const editingPatch = await this.$store.dispatch('design/setPatchForEditing', this.$route.query.patchId);

          Promise.all([colorsCategories, editingPatch]).then(() => {
            svgApi.loadSvg(this.patchForEditing.fileUrl).then((response) => {
              this.apartSvgPatchText = response;
              this.initPatch();
            });
          });
        } else if (this.$route.name === 'InsertEditor') {
          const editingInsert = this.$store.dispatch('design/setInsertForEditing', this.$route.query.insertId);
          Promise.all([colorsCategories, editingInsert]).then(() => {
            svgApi.loadSvg(this.insertForEditing.fileUrl).then((response) => {
              this.apartSvgPatchText = response;
              this.initPatch();
            });
          });
        }
      } else {
        const editingPatch = await this.$store.dispatch('design/setPatchForEditing', this.originalPatchId);

        Promise.all([colorsCategories, editingPatch]).then(() => {
          this.initPatch();
        });

        this.textColor = { hex: '#ffffff' };
        this.activeSide = this.jacketSide;
      }
    },

    initPatch () {
      let patchSvgText;

      if ((this.$route.name === 'PatchEditor' || this.$route.name === 'InsertEditor')) {
        patchSvgText = this.apartSvgPatchText;
      } else {
        patchSvgText = this.patchSvgText;
      }

      if (!patchSvgText) return false;
      if (this.isPending) return false;

      setTimeout(() => {
        this.initPatchCanvas(patchSvgText);
        this.addEventsToElements();
        const patch = this.findSVGPatch();
        if (!patch) {
          console.log('Patch not found!');
          return false;
        }

        this.getColorsFromPatch();
        this.pendingApartSvgPatchText = false;

        return true;
      }, 0);

      this.pendingApartSvgPatchText = false;
      return true;
    },

    loadPatch () {
      return svgApi.loadSvg(this.activePatch.fileUrl);
    },

    initPatchCanvas (svgText) {
      let patchId;
      if (this.$route.name === 'PatchEditor') {
        patchId = this.patchForEditing.id;
      } else if (this.$route.name === 'InsertEditor') {
        patchId = this.insertForEditing.id;
      } else {
        patchId = this.patchId ? this.patchId : uniqueId('project-patch');
      }

      this.canvasId = patchId;
      this.canvas = SVG('svg-container').size(600, 500);
      this.canvas.id(this.canvasId);
      if (this.patchId) {
        this.canvas.svg(svgText);
        const groupWrapper = this.canvas.select(`#${this.canvasId}--wrapper`).first();
        this.canvas.size(groupWrapper.bbox().w, groupWrapper.bbox().h);
      } else {
        const groupWrapper = this.canvas.group().id(`${this.canvasId}--wrapper`);
        groupWrapper.svg(svgText);
        this.canvas.size(groupWrapper.bbox().w, groupWrapper.bbox().h);
      }

      this.canvas.on('click', () => {
        this.unselectTextElements();
      });
    },

    addEventsToElements () {
      this.canvas.select('.text-element').members.forEach((textElement) => {
        this.addEventsToText(textElement);
        this.textElements.push(textElement);
      });

      this.canvas.select('.insert-element').members.forEach((insertElement) => {
        this.addEventsToInsert(insertElement);
        this.insertElements.push(insertElement);
      });
    },

    findSVGPatch () {
      const svg = this.canvas.select('svg').get(0);
      if (svg.id() !== 'editing-patch') {
        svg.id('editing-patch');
      }
      return svg;
    },

    getColorsFromPatch () {
      const colors = this.getColorsByProperty('editing-patch', 'FILL', 'fill');
      const stroke = this.getColorsByProperty('editing-patch', 'STROKE', 'stroke');

      if (stroke.length !== 0) {
        colors.splice(2, 0, stroke);
      }
      const flattenColors = flattenDeep(colors);
      this.colors = flattenColors;

      if ((this.$route.name === 'PatchEditor' || this.$route.name === 'InsertEditor')) {
        this.colors = flattenColors.map((color, index) => {
          const tagMaps = this.patchForEditing?.tagsMap;
          const tag = (tagMaps && tagMaps[color.layerId]) ? tagMaps[color.layerId] : 'static';
          this.tagsMap = { ...this.tagsMap, [`${color.layerId}`]: tag };

          return { ...color, id: index, [`${color.layerId}`]: tag };
        }).sort((a, b) => (a.id > b.id ? 1 : -1));
      }

      this.colors.forEach((item) => {
        if (item.order !== null) {
          this.colors.sort((a, b) => (a.order > b.order ? 1 : -1));
        }
      });

      if (this.schoolColors) {
        const tagMaps = this.patchForEditing?.tagsMap;

        this.colors.forEach((colorItem) => {
          const colorLayer = colorItem.layerId;
          const colorTag = tagMaps[colorLayer];

          if (colorTag && colorTag !== 'static') {
            // eslint-disable-next-line no-param-reassign
            colorItem.color = this.schoolColors[colorTag];
            this.changeSvgElementColor('editing-patch', { hex: this.schoolColors[colorTag] }, colorItem.layerId);
          }
        });
      }
    },

    onChangeTag (tagLayer, tagValue) {
      this.tagsMap[tagLayer] = tagValue;

      this.colors.forEach((colorItem) => {
        if ((colorItem.layerId === tagLayer) && tagValue !== 'static') {
          // eslint-disable-next-line no-param-reassign
          colorItem.color = (this.schoolColors && this.schoolColors[tagValue])
            ? this.schoolColors[tagValue] : colorItem.color;
        }
      });

      if (this.schoolColors && tagValue !== 'static') {
        this.changeSvgElementColor('editing-patch', { hex: this.schoolColors[tagValue] }, tagLayer);
      }
    },

    onChangeColor (newColor, layerId) {
      this.changeSvgElementColor('editing-patch', newColor, layerId);
      this.colors = this.changeColorsData(this.colors, layerId, newColor);
    },

    onChangeText (text) {
      this.text = text;
      if (this.activeTextElement) {
        this.activeTextElement.text(text);
        this.selectTextElement(this.activeTextElement);
      }
    },

    onChangeTextColor (textColor) {
      this.textColor = textColor;
      if (this.activeTextElement) {
        this.activeTextElement.font({ fill: textColor.hex });
      }
    },

    onChangeFontSize (fontSize) {
      this.textSize = fontSize;
      if (this.activeTextElement) {
        this.activeTextElement.font({ size: fontSize * ONE_INCH_IN_PX });
      }
    },

    onClickOutsideCanvas (e) {
      if (e.target.className !== 'color-item__color') {
        this.unselectAll();
      }
    },

    unselectAll () {
      this.unselectTextElements();
      this.unselectInsertElements();
    },

    unselectTextElements () {
      this.textElements.forEach((figure) => figure.selectize(false));
      this.resetActiveTextElement();
    },

    resetActiveTextElement () {
      this.activeTextElement = null;
      this.text = '';
      this.textSize = 0.3;
      this.textColor = { hex: '#ffffff' };
    },

    selectTextElement (element) {
      this.textElements.forEach((figure) => figure.selectize(false));
      this.insertElements.forEach((insert) => insert.selectize(false));
      this.activeInsertElement = null;
      element
        .front()
        .selectize({ rotationPoint: true })
        .resize(false);
    },

    onSelectTextElement (textElement) {
      const vm = this;
      textElement.mousedown(function (event) {
        event.stopPropagation();
        vm.selectTextElement(this);
        vm.activeTextElement = this;
        vm.text = this.text();
        vm.textColor = vm.colorsCategories.patchColors
          .find((color) => color.hex.toLowerCase() === this.font().fill);
        vm.textSize = parseFloat(
          (this.font().size / ONE_INCH_IN_PX).toFixed(1)
        );
      });
    },

    onAddText (text, textColor, textSize) {
      const textElement = this.canvas.select(`#${this.canvasId}--wrapper`).members[0]
        .text('')
        .id(uniqueId('text-element_'))
        .addClass('text-element')
        .font({ fill: textColor.hex, size: textSize * ONE_INCH_IN_PX })
        .text(text)
        .move(this.canvas.bbox().cx, this.canvas.bbox().cy);

      textElement.selectize({ rotationPoint: true });
      this.addEventsToText(textElement);
      this.textElements.push(textElement);
      this.unselectTextElements();
    },

    addEventsToText (group) {
      group
        .draggable(true, {
          minX: 0,
          minY: 0,
          maxX: this.canvas.width(),
          maxY: this.canvas.height()
        })
        .on('delete', () => {
          this.onRemoveText();
        });
      this.onSelectTextElement(group);
    },

    onRemoveText () {
      if (this.activeTextElement) {
        const elementToRemove = this.activeTextElement;
        this.unselectTextElements();

        this.textElements = this.textElements.filter(
          (textElement) => textElement.id() !== elementToRemove.id()
        );
        elementToRemove.remove();
      }
    },

    addInsertOnPatch (insertId, width, height, x, y, isCenter) {
      const insert = this.inserts[insertId];
      const id = uniqueId(`insert-${insertId}__`);
      const insertElement = this.createInsertSvg(insert.svgText, true, id, width, height);
      if (!isCenter) {
        insertElement.x(x).y(y);
      } else {
        insertElement.cx(x).cy(y);
      }
      this.addEventsToInsert(insertElement);
      this.insertElements.push(insertElement);
    },

    addEventsToInsert (insertElement) {
      insertElement.draggable(true, {
        minX: 0,
        minY: 0,
        maxX: this.canvas.width(),
        maxY: this.canvas.height()
      });

      insertElement.on('delete', () => {
        this.onRemoveInsert();
      });

      this.onSelectInsertElement(insertElement);
    },

    handleInsertDrop (transferData, event) {
      const {
        insertId,
        offsetX, offsetY,
        width, height
      } = transferData;
      const canvasBound = this.canvas.node.getBoundingClientRect();
      const x = event.clientX - canvasBound.left - offsetX;
      const y = event.clientY - canvasBound.top - offsetY;

      this.addInsertOnPatch(insertId, width, height, x, y, false);
    },

    onAddInsert (options) {
      const {
        insertId, width, height
      } = options;
      const canvasBbox = this.canvas.bbox();
      this.addInsertOnPatch(insertId, width, height, canvasBbox.cx, canvasBbox.cy, true);
    },

    onSelectInsertElement (insertElement) {
      const vm = this;
      insertElement.mousedown(function (event) {
        vm.textElements.forEach((figure) => figure.selectize(false));
        vm.resetActiveTextElement();

        event.stopPropagation();

        vm.unselectInsertElements();
        this
          .front()
          .selectize({ canResize: false })
          .resize();

        vm.activeInsertElement = this;
        vm.getInsertColors(vm.activeInsertElement);
      });
    },

    unselectInsertElements () {
      this.insertElements.forEach((figure) => figure.selectize(false));
      this.activeInsertElement = null;
      this.$store.commit('design/setActiveInsertColors', []);
    },

    onRemoveInsert () {
      const elementToRemove = this.activeInsertElement;
      if (elementToRemove) {
        this.unselectInsertElements();

        this.insertElements = this.insertElements.filter(
          (element) => element.id() !== elementToRemove.id()
        );
        elementToRemove.remove();
      }
    },

    onChangeInsertColor (options) {
      const {
        newColor,
        layerId,
        colors
      } = options;

      this.changeSvgElementColor(this.activeInsertElement.id(),
        newColor, layerId);
      this.insertColors = colors;
      this.$store.commit('design/setActiveInsertColors', this.insertColors);
    },

    getInsertColors (insertElement) {
      const colors = this.getColorsByProperty(insertElement.id(), 'FILL', 'fill');
      const stroke = this.getColorsByProperty(insertElement.id(), 'STROKE', 'STROKE');
      if (stroke.length) {
        colors.splice(1, 0, stroke[0]);
      }
      this.setActiveInsertColors(colors);
    },

    setActiveInsertColors (colors) {
      this.insertColors = colors.map((color, index) => {
        return { ...color, id: index };
      }).sort((a, b) => (a.id > b.id ? 1 : -1));

      this.insertColors.forEach((item) => {
        if (item.order !== null) {
          this.insertColors.sort((a, b) => (a.order > b.order ? 1 : -1));
        }
      });

      this.$store.commit('design/setActiveInsertColors', this.insertColors);
    },

    cancel () {
      this.$bvModal.hide(this.modalId);
    },

    apartCancel () {
      if (this.$route.name === 'PatchEditor') {
        window.location.href = `${process.env.VUE_APP_REDIRECT_HOST}/Patches/Edit/${this.$route.query.patchId}`;
      }

      if (this.$route.name === 'InsertEditor') {
        window.location.href = `${process.env.VUE_APP_REDIRECT_HOST}/Inserts/Edit/${this.$route.query.insertId}`;
      }
    },

    takePatchScreenshot () {
      document.getElementById('svg-container').style.fontFamily = "'Brisen', 'Freshman', 'cloistrk', 'BrisenTail'";
      return htmlToImage.toPng(document.getElementById('svg-container'), { pixelRatio: 1 }).then((base64image) => {
        return base64image;
      });
    },

    saveInserts () {
      this.savingInserts = this.insertElements.map((elem) => {
        const insertId = elem.id();
        const originalInsertId = insertId.substring(insertId.indexOf('-') + 1, insertId.indexOf('__'));
        let insert;
        if (this.inserts[originalInsertId]) {
          insert = this.inserts[originalInsertId];
        } else {
          insert = {
            ...this.patches.data[this.patchId].inserts.find((item) => {
              return item.id === insertId;
            })
          };
        }
        const colors = this.getColorsByProperty(insertId, 'FILL', 'fill');
        const stroke = this.getColorsByProperty(insertId, 'STROKE', 'STROKE');
        if (stroke.length) {
          colors.splice(1, 0, stroke[0]);
        }

        const endingColors = colors.map((color, index) => {
          return { ...color, id: index };
        });

        const { sellingPrice } = this.insertCategories.data[insert.categoryId];
        return {
          id: insertId,
          originalInsertId,
          name: insert.name,
          categoryId: insert.categoryId,
          colors: endingColors,
          sellingPrice
        };
      });
    },

    saveApartPatchOrInsert () {
      this.pendingApartPatchSaving = true;

      let patchId;
      let insertId;
      let schoolId;

      if (this.$route.query.patchId) {
        patchId = this.$route.query.patchId;
      }

      if (this.$route.query.insertId) {
        insertId = this.$route.query.insertId;
      }

      if (this.$route.query.schoolId) {
        schoolId = this.$route.query.schoolId;
      } else {
        schoolId = null;
      }

      const initialSvgCanvas = this.canvas.select('svg').get(0).id('Layer_1');
      const initialSvg = initialSvgCanvas.node;

      const serializedSVG = new XMLSerializer().serializeToString(initialSvg);
      const base64Data = window.btoa(serializedSVG);
      const fileContent = `data:image/svg+xml;base64,${base64Data}`;

      if (this.$route.name === 'PatchEditor') {
        this.$store.dispatch('design/saveApartPatch', {
          patchId,
          schoolId,
          fileContent,
          tagsMap: this.tagsMap
        }).then(() => {
          window.location.href = `${process.env.VUE_APP_REDIRECT_HOST}/Patches/Edit/${this.updatedPatchData.id}`;
          if (!window.location.href.includes('patchEditor')) {
            this.pendingApartPatchSaving = false;
          }
        });
      }

      if (this.$route.name === 'InsertEditor') {
        this.$store.dispatch('design/saveApartInsert', { insertId, fileContent }).then(() => {
          window.location.href = `${process.env.VUE_APP_REDIRECT_HOST}/Inserts/Edit/${this.updatedInsertData.id}`;
          if (!window.location.href.includes('insertEditor')) {
            this.pendingApartPatchSaving = false;
          }
        });
      }
    },
    save () {
      this.isSavingPatch = true;
      this.unselectAll();
      this.takePatchScreenshot().then((screenshot) => {
        const id = this.canvasId;
        const svgText = this.canvas.svg();
        let name;
        let categoryId;
        let patchSize;
        let side;
        let availableSizes;
        if (!this.patchId) {
          name = this.patchName;
          categoryId = this.patchCategoryId;
          patchSize = this.size;
          side = this.activeSide;
          availableSizes = this.availableSizes;
          this.$store.dispatch('design/setPatchIdBySide', { id, side: this.activeSide });
        } else {
          name = this.patchesBySide(side).data[this.patchId].name;
          categoryId = this.patchesBySide(side).data[this.patchId].categoryId;
          patchSize = this.patchesBySide(side).data[this.patchId].patchSize;
          side = this.patchesBySide(side).data[this.patchId].side;
          availableSizes = this.patchesBySide(side).data[this.patchId].availableSizes;
        }

        this.saveInserts();

        const action = this.patchId ? 'design/updatePatch' : 'design/addPatchToProject';

        this.$store.dispatch(action, {
          id,
          originalId: this.originalPatchId,
          svgText,
          name,
          categoryId,
          patchSize,
          side,
          availableSizes,
          colors: this.colors,
          inserts: this.savingInserts,
          screenShot: screenshot,
          tagsMap: this.tagsMap
        });

        this.$store.dispatch('finalizeOrder/calculateTotalPrice');

        setTimeout(() => {
          const eventName = this.patchId ? 'Global_updatePatchInProject' : 'Global_addPatchToProject';

          this.$events.emit(eventName, { patchId: id });
        }, 0);
        this.isSavingPatch = false;
        this.$bvModal.hide(this.modalId);
      });
    }
  }
};
</script>

<style scoped>

.differentEdit {
  padding: 20px;
  background: #e6e7e8;
  border-radius: 0.3rem;
  min-height: 800px;
  justify-content: space-between;
}

.svg-dropzone {
  display: flex;
  justify-content: center;
}

#svg-container {
  height: auto;
}

.editor-colors {
  left: 15px;
  position: absolute;
}

.editor-loader {
  left: 50%;
  position: absolute;
}
</style>
